import { useState, useEffect, type JSX } from 'react';
import { 
  Container, 
  Title, 
  Grid, 
  Paper, 
  Text, 
  Group, 
  Alert,
  Loader,
  useMantineTheme,
  Box
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { showNotification } from '@mantine/notifications';

import { ReflectionThreadList } from '../components/reflection/ReflectionThreadList';
import { ClientReflectionChat } from '../components/reflection/ClientReflectionChat';
import { WalkthroughManager } from '../components/reflection/WalkthroughManager';
import { ConversationLimitBanner } from '../components/ConversationLimitBanner';
import { NewConversationButton } from '../components/NewConversationButton';
import { UpgradeModal } from '../components/UpgradeModal';
import { useClientReflectionThreads } from '../hooks/useClientReflectionThreads';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

/**
 * Main page for the client-facing Reflection Guide feature
 * Allows clients to create and continue reflection conversations
 */
interface ReflectionGuidePageProps {
  navbarState?: {
    opened: boolean;
    setOpened: (value: boolean) => void;
  };
}

export function ReflectionGuidePage({ navbarState }: ReflectionGuidePageProps): JSX.Element {
  console.log('Main ReflectionGuidePage rendered');
  const medplum = useMedplum();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const { threads, isLoading, createNewThread } = useClientReflectionThreads();
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const { subscriptionTier, isPremium, isBasic, isFree } = useSubscriptionFeatures();
  
  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(false);
  const [isFullScreenChat, setIsFullScreenChat] = useState(false);
  
  // Debug log to verify subscription features
  console.log('[ReflectionGuidePage] Subscription features:', { 
    subscriptionTier, 
    isPremium, 
    isBasic, 
    isFree,
    // Force isFree to true if tier is 'free', as a fallback
    shouldForceFree: subscriptionTier === 'free' && !isFree
  });
  
  // Force correct tier permissions if there's a mismatch
  const actualIsFree = isFree || subscriptionTier === 'free';
  const actualIsBasic = isBasic || subscriptionTier === 'basic';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<'voice' | 'limit'>('voice');
  
  // Theme for breakpoints
  const theme = useMantineTheme();
  
  // Media query to detect mobile screens
  const isMobileScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) || false;
  
  // Set mobile state based on screen size and active conversation
  useEffect(() => {
    setIsMobile(isMobileScreen);
    
    // If on mobile and a thread is selected, go to full screen chat mode
    setIsFullScreenChat(isMobileScreen && selectedThreadId ? true : false);
    
    // Update the navbar with thread data
    if (navbarState && navbarState.opened) {
      // Keep track of selected thread and threads in the navbar component
      const formattedThreads = threads.map(thread => ({
        id: thread.id,
        name: thread.topic || 'Reflection Session',
        date: thread.lastMessageSentAt || new Date()
      }));
      
      // Pass thread data to navbar when needed
      // This example just logs it - in a real implementation you'd use a context or state management
      console.log('Updating navbar with thread data', formattedThreads);
    }
  }, [isMobileScreen, selectedThreadId, threads, navbarState]);
  
  // Function to create a new thread
  const handleNewThread = async () => {
    // NewConversationButton will handle the subscription check
    
    try {
      setIsCreatingThread(true);
      const newThreadId = await createNewThread('Self-Guided Reflection Session');
      setSelectedThreadId(newThreadId);
    } catch (error) {
      showNotification({
        title: 'Error',
        message: `Failed to create new reflection thread: ${(error as Error).message}`,
        color: 'red'
      });
      console.error('Error creating new reflection thread:', error);
    } finally {
      setIsCreatingThread(false);
    }
  };

  // Check if the user is logged in and is a client
  const profile = medplum.getProfile();
  const isClient = profile?.resourceType === 'Patient'; // Still using Patient resource internally

  if (!profile) {
    return (
      <Container>
        <Paper p="xl" withBorder mt="xl">
          <Text ta="center">Please sign in to access the Reflection Guide.</Text>
        </Paper>
      </Container>
    );
  }

  if (!isClient) {
    return (
      <Container>
        <Paper p="xl" withBorder mt="xl">
          <Text ta="center">The Reflection Guide is only available for clients.</Text>
        </Paper>
      </Container>
    );
  }
  
  // Function to exit full screen chat mode (now handled by navbar)
  const exitFullScreenChat = () => {
    setIsFullScreenChat(false);
    // On mobile, this would typically go back to the thread list
    if (isMobile) {
      setSelectedThreadId(null);
      // Also open the navbar if available
      if (navbarState) {
        navbarState.setOpened(true);
      }
    }
  };

  // Render the full content of the app
  const renderFullContent = () => (
    <>
      {/* WalkthroughManager for tutorial/onboarding */}
      {(() => {
        console.log('Rendering WalkthroughManager in main ReflectionGuidePage');
        return <WalkthroughManager />;
      })()}
      
      {/* Title only shown when not in full-screen mode */}
      {(!isMobile || !isFullScreenChat) && (
        <Title order={2} my="md" style={{ fontFamily: 'Poppins, sans-serif' }}>Reflection Guide</Title>
      )}
      
      {/* Only show on non-fullscreen or desktop mode */}
      {(!isMobile || !isFullScreenChat) && (
        <>
          {/* Add the conversation limit banner */}
          <ConversationLimitBanner />
          
          <Paper p="md" withBorder mb="md">
            <Text mb="md">
              Welcome to your Reflection Guide. This AI-powered guide helps you explore your thoughts and feelings. You can start a new conversation anytime.
            </Text>
            
            <Text size="sm" color="dimmed">
              We're glad you're here.
            </Text>
          </Paper>
        </>
      )}
      
      <Grid gutter={isMobile && isFullScreenChat ? 0 : undefined}>
        {/* Sidebar - Hidden on mobile when in full-screen chat mode */}
        {(!isMobile || !isFullScreenChat) && (
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Group justify="space-between" mb="md">
              <Text fw={500}>Recent Sessions</Text>
              <NewConversationButton
                onStartTextConversation={handleNewThread}
                size="xs" 
                variant="filled"
                className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white rounded-full"
                buttonText="New"
              />
            </Group>
            
            <ReflectionThreadList 
              threads={threads.slice(0, 3)}
              isLoading={isLoading}
              selectedThreadId={selectedThreadId}
              onSelectThread={(threadId) => {
                setSelectedThreadId(threadId);
                // Automatically go to full screen on mobile
                if (isMobile) {
                  setIsFullScreenChat(true);
                }
              }} 
            />
          </Grid.Col>
        )}
        
        {/* Main Content - Full width on mobile in chat mode */}
        <Grid.Col 
          span={
            isMobile && isFullScreenChat 
              ? 12 // Full width on mobile in chat mode
              : { base: 12, md: 9 } // Normal responsive behavior otherwise
          }
          p={isMobile && isFullScreenChat ? 0 : undefined} // Remove padding when in fullscreen
          style={{
            height: isMobile && isFullScreenChat ? '100%' : 'auto',
            margin: isMobile && isFullScreenChat ? 0 : undefined,
          }}
        >
          {selectedThreadId ? (
            (() => {
              console.log('[ReflectionGuidePage] Rendering ClientReflectionChat with props:', {
                threadId: selectedThreadId,
                showUpgradeForMessage: actualIsFree,
                showUpgradeForVoice: actualIsFree || actualIsBasic,
                isMobileFullscreen: isMobile && isFullScreenChat
              });
              return (
                <ClientReflectionChat 
                  threadId={selectedThreadId}
                  showUpgradeForMessage={actualIsFree}
                  showUpgradeForVoice={actualIsFree || actualIsBasic}
                  isMobileFullscreen={isMobile && isFullScreenChat}
                />
              );
            })()
          ) : (
            <Paper p="xl" withBorder>
              <Text ta="center" mb="lg">
                Start a new reflection session or select a recent one.
              </Text>
              
              <Group justify="center">
                <NewConversationButton
                  onStartTextConversation={handleNewThread} 
                  size="md"
                  fullWidth={false}
                  variant="filled"
                  className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white rounded-full"
                />
              </Group>
            </Paper>
          )}
        </Grid.Col>
      </Grid>
      
      {/* Only show info on non-fullscreen or desktop mode */}
      {(!isMobile || !isFullScreenChat) && (
        <Alert 
          icon={<IconInfoCircle size={16} />} 
          color="blue" 
          variant="light"
          mt="xl"
        >
          <Text size="sm" fw={700}>
            About Reflection Guide
          </Text>
          <Text size="sm" mt="xs">
            The Reflection Guide uses AI to help with thoughtful conversation and reflection. 
            It is not a replacement for professional therapy, but a tool to support personal exploration.
          </Text>
        </Alert>
      )}
    </>
  );
  
  // Pass mobile fullscreen state to parent context if needed
  useEffect(() => {
    // Inform parent components (App) about fullscreen state
    if (navbarState && isMobile && isFullScreenChat) {
      // Set a data attribute on the body element that can be used for CSS targeting
      document.body.setAttribute('data-mobile-fullscreen', 'true');
    } else {
      document.body.removeAttribute('data-mobile-fullscreen');
    }
    
    return () => {
      document.body.removeAttribute('data-mobile-fullscreen');
    };
  }, [isMobile, isFullScreenChat, navbarState]);

  return (
    <Container 
      fluid={isMobile && isFullScreenChat} // Use fluid container to remove horizontal padding
      style={{ 
        padding: isMobile && isFullScreenChat ? 0 : undefined,
        margin: isMobile && isFullScreenChat ? 0 : undefined,
        maxWidth: isMobile && isFullScreenChat ? '100%' : undefined,
        // When in fullscreen mode on mobile, use the full viewport height minus header
        height: isMobile && isFullScreenChat ? 'calc(100vh - 80px)' : undefined,
        // Fill vertical space when in fullscreen
        display: isMobile && isFullScreenChat ? 'flex' : undefined,
        flexDirection: 'column'
      }}
    >
      {/* Main content */}
      {renderFullContent()}

      {/* Upgrade Modal */}
      <UpgradeModal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentTier={subscriptionTier}
      />
    </Container>
  );
}